import { initials } from './shared/initials.ts';
import { useEffect, useState, type FormEvent } from 'react';
import { RpcError, type SignInMethod, type SignInResult, type TwoFactorMethod } from '@flowology/client';
import { client } from './api.ts';
import { TwoFactorStep } from './account/TwoFactorStep.tsx';
import { useDesign } from './blueprint/design.ts';
import './blueprint/login.css';

// Off a company's own hostname (local development) the company is named in the form.
const onCompanyHost = !/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

const ALL: readonly SignInMethod[] = ['password', 'google', 'microsoft', 'email_code', 'sms_code'];
const PROVIDER_ERRORS: Record<string, string> = {
  google_failed: 'Google couldn’t sign you in. Try again, or use another way below.',
  microsoft_failed: 'Microsoft couldn’t sign you in. Try again, or use another way below.',
  // Unlinked by its person (QA-200): it comes back only by linking it again from Your sign-in.
  google_unlinked:
    'This Google account isn’t linked to Flowology. Sign in another way, then link it in Your sign-in.',
  microsoft_unlinked:
    'This Microsoft account isn’t linked to Flowology. Sign in another way, then link it in Your sign-in.',
};

type Mode = { kind: 'password' } | { kind: 'code'; channel: 'email' | 'sms'; sent: string | null };

// The providers' own marks, as their sign-in button guidelines ask: Google's multicolour "G" on a light or
// neutral button, Microsoft's four squares beside "Continue with Microsoft". Inline, no external images; the
// colours are the providers' brand colours, not ours, so they are the one place hex values are allowed here.
function GoogleMark() {
  return (
    <svg
      className="provider-mark"
      width="18"
      height="18"
      viewBox="0 0 48 48"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
function MicrosoftMark() {
  return (
    <svg
      className="provider-mark"
      width="18"
      height="18"
      viewBox="0 0 21 21"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/**
 * Signing in (decision 19): a password, Google, or a one-time code by email or text, as the company
 * allows (/auth/methods). The answers never say whether an email or mobile is known.
 */
export function Login({
  onSignedIn,
  tenant: knownTenant,
  again = false,
  brand,
}: {
  /** recoveryCodesLeft: set when the second step was a recovery code, so the app can say how many remain. */
  onSignedIn: (csrf: string, tenant: string, recoveryCodesLeft?: number) => void;
  /** Signing in again after the session ended: the company is known and the screen stays behind. */
  tenant?: string;
  again?: boolean;
  /**
   * The company, once it is known before sign-in (the company-host branding lookup, still to come): its
   * name for the "to <company>" line and its logo stone. Without it the page is unbranded, as today.
   */
  brand?: { name: string; logoUrl?: string | null };
}) {
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tenant, setTenant] = useState(knownTenant ?? '');
  const [methods, setMethods] = useState<readonly SignInMethod[]>(ALL);
  const [mode, setMode] = useState<Mode>({ kind: 'password' });
  const [error, setError] = useState<string | null>(() => {
    const why = new URLSearchParams(window.location.search).get('signin');
    if (!why) return null;
    window.history.replaceState(null, '', window.location.pathname); // a reload starts clean
    return PROVIDER_ERRORS[why] ?? 'That sign-in didn’t work. Try again, or use another way below.';
  });
  const [busy, setBusy] = useState(false);
  // The second factor still owed after the first (decision 20, QA-093).
  const [second, setSecond] = useState<readonly TwoFactorMethod[] | null>(null);
  // Whose sign-in the second step is for: the first factor said (rs-portal's QA-093 review).
  const [stepCompany, setStepCompany] = useState<string | null>(null);
  const company = onCompanyHost ? undefined : tenant.trim() || undefined;
  const owed = (methods: readonly TwoFactorMethod[], name?: string) => {
    setStepCompany(name || null);
    setSecond(methods);
  };
  const shown = brand ?? (second && stepCompany ? { name: stepCompany, logoUrl: null } : undefined);
  // In Blueprint (pages/ux-full signin.html) the page is one ruled sheet on the desk (the frame's
  // .bp-sheet), and the company is named beside its initials above the title.
  const bp = useDesign() === 'blueprint';
  const done = (r: SignInResult) =>
    r.twoFactor ? owed(r.twoFactor.methods, r.twoFactor.company) : onSignedIn(r.csrf, r.tenant);

  // Back from Google, Microsoft or a one-tap link with a second factor owed: ask for it here.
  useEffect(() => {
    if (again || (!onCompanyHost && !company)) return;
    client
      .twoFactorPending(company)
      .then((p) => p.pending && p.methods && owed(p.methods, p.company))
      .catch(() => undefined);
  }, [company, again]);

  // What this company allows; until it answers (or off a company host before one is named), all.
  useEffect(() => {
    if (!onCompanyHost && !company) return setMethods(ALL);
    const t = setTimeout(() => {
      client
        .signInMethods(company)
        .then((r) => {
          setMethods(r.methods.length ? r.methods : ALL);
          if (!r.methods.includes('password') && mode.kind === 'password')
            setMode({
              kind: 'code',
              channel: r.methods.includes('sms_code') && !r.methods.includes('email_code') ? 'sms' : 'email',
              sent: null,
            });
        })
        .catch(() => setMethods(ALL));
    }, 300);
    return () => clearTimeout(t);
  }, [company]); // re-asked only when the company changes

  const fail = (err: unknown) =>
    setError(err instanceof RpcError ? err.message : 'Could not reach Flowology. Check your connection.');

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      done(await client.signIn(email, password, company));
    } catch (err) {
      setPassword('');
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (mode.kind !== 'code') return;
    setBusy(true);
    setError(null);
    const to = mode.channel === 'email' ? { email: email.trim() } : { mobile: mobile.trim() };
    try {
      if (!mode.sent) {
        const r = await client.startCode({ ...to, ...(company ? { tenant: company } : {}) });
        setMode({ ...mode, sent: r.message });
      } else {
        done(
          await client.verifyCode({
            ...to,
            code: code.trim(),
            ...(company ? { tenant: company } : {}),
          }),
        );
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  const google = methods.includes('google');
  const microsoft = methods.includes('microsoft');
  const byEmail = methods.includes('email_code');
  const byText = methods.includes('sms_code');
  const byPassword = methods.includes('password');
  const companyField = !onCompanyHost && !again && (
    <label>
      Company
      <input
        className="field"
        value={tenant}
        onChange={(e) => setTenant(e.target.value)}
        autoComplete="organization"
        aria-describedby="login-company-hint"
        required
      />
      <span id="login-company-hint" className="help">
        The company name from your invite
      </span>
    </label>
  );
  // the refusal sits above everything, in the bad tone (its text is the server's answer, unchanged)
  const banner = error && (
    <p className="banner bad login-banner" role="alert">
      {error}
    </p>
  );

  return (
    <main
      className={again ? 'login again' : bp ? 'login bp-sheet' : 'login'}
      role={again ? 'dialog' : undefined}
      aria-modal={again || undefined}
    >
      <div className="sheet login-sheet">
        {shown && bp ? (
          <p className="login-co">
            <span className="logo login-logo" aria-hidden="true">
              {shown.logoUrl ? <img src={shown.logoUrl} alt="" /> : initials(shown.name)}
            </span>
            {shown.name}
          </p>
        ) : (
          shown && (
            <span className="logo login-logo" aria-hidden="true">
              {shown.logoUrl ? <img src={shown.logoUrl} alt="" /> : initials(shown.name)}
            </span>
          )
        )}
        {!second && <h1>Sign in</h1>}
        {brand && !second && !bp && <p className="lede login-lede">to {brand.name}</p>}
        {again && <p className="muted">Your session ended. Sign in again to carry on where you were.</p>}
        {banner}
        {second ? (
          <TwoFactorStep
            methods={second}
            {...(shown ? { company: shown.name } : {})}
            {...(company ? { tenant: company } : {})}
            onSignedIn={onSignedIn}
            onCancel={() => {
              setSecond(null);
              setStepCompany(null);
              setPassword('');
              setCode('');
            }}
          />
        ) : (
          <>
            {(google || microsoft) && (
              <>
                <div className="login-providers" role="group" aria-label="Sign in with">
                  {google && (
                    <a
                      className="btn wide outline"
                      href={`/auth/google/start${company ? `?tenant=${encodeURIComponent(company)}` : ''}`}
                    >
                      <GoogleMark />
                      Continue with Google
                    </a>
                  )}
                  {microsoft && (
                    <a
                      className="btn wide outline"
                      href={`/auth/microsoft/start${company ? `?tenant=${encodeURIComponent(company)}` : ''}`}
                    >
                      <MicrosoftMark />
                      Continue with Microsoft
                    </a>
                  )}
                </div>
                <p className="login-or" aria-hidden="true">
                  or
                </p>
              </>
            )}
            {mode.kind === 'password' ? (
              <form className="login-form" onSubmit={(e) => void submitPassword(e)}>
                {companyField}
                <label>
                  Email
                  <input
                    className="field"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </label>
                {byPassword && (
                  <label>
                    Password
                    <input
                      className="field"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </label>
                )}
                {byPassword && (
                  <button className="btn primary wide lg" disabled={busy}>
                    {busy ? 'Signing in…' : 'Sign in'}
                  </button>
                )}
              </form>
            ) : (
              <form className="login-form" onSubmit={(e) => void submitCode(e)}>
                {companyField}
                {mode.channel === 'email' ? (
                  <label>
                    Email
                    <input
                      className="field"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="username"
                      required
                      readOnly={!!mode.sent}
                    />
                  </label>
                ) : (
                  <label>
                    Mobile
                    <input
                      className="field"
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      autoComplete="tel"
                      required
                      readOnly={!!mode.sent}
                    />
                  </label>
                )}
                {mode.sent && (
                  <>
                    <p className="notice ok" role="status">
                      {mode.sent}
                    </p>
                    <label>
                      Code
                      <input
                        className="field"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        required
                      />
                    </label>
                  </>
                )}
                <button className="btn primary wide lg" disabled={busy}>
                  {busy
                    ? 'One moment…'
                    : mode.sent
                      ? 'Sign in'
                      : mode.channel === 'email'
                        ? 'Email me a code'
                        : 'Text me a code'}
                </button>
                {mode.sent && (
                  <button
                    type="button"
                    className="btn quiet"
                    onClick={() => setMode({ ...mode, sent: null })}
                  >
                    Send a new code
                  </button>
                )}
              </form>
            )}
            <div className="login-other" role="group" aria-label="Other ways to sign in">
              {byEmail && !(mode.kind === 'code' && mode.channel === 'email') && (
                <button
                  type="button"
                  className="lnk"
                  onClick={() => {
                    setError(null);
                    setMode({ kind: 'code', channel: 'email', sent: null });
                  }}
                >
                  Email me a code
                </button>
              )}
              {byText && !(mode.kind === 'code' && mode.channel === 'sms') && (
                <button
                  type="button"
                  className="lnk"
                  onClick={() => {
                    setError(null);
                    setMode({ kind: 'code', channel: 'sms', sent: null });
                  }}
                >
                  Text me a code
                </button>
              )}
              {!byEmail && mode.kind === 'password' && (
                // The owner can always get in with an email code, whatever the company allows.
                <button
                  type="button"
                  className="lnk"
                  onClick={() => {
                    setError(null);
                    setMode({ kind: 'code', channel: 'email', sent: null });
                  }}
                >
                  Company owner? Email me a code
                </button>
              )}
              {byPassword && mode.kind === 'code' && (
                <button
                  type="button"
                  className="lnk"
                  onClick={() => {
                    setError(null);
                    setMode({ kind: 'password' });
                  }}
                >
                  Use my password
                </button>
              )}
              {byPassword && byEmail && mode.kind === 'password' && (
                <p className="small muted">Forgot your password? An email code signs you in.</p>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
